import React, { useState } from "react";
import {
  Sun,
  Thermometer,
  CloudRain,
  Wind,
  Check,
  X,
  MoveHorizontal,
  AlertCircle,
  Lock,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";

// SVG Components for Visualizations
const EnergyBalanceVisualization = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-40 h-40 rounded-full bg-blue-100 border-4 border-blue-200 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse"></div>
      </div>
    </div>
    <div className="absolute top-5 right-20">
      <Sun className="h-16 w-16 text-yellow-500" />
      <div className="absolute top-8 -left-20 w-24 h-1 bg-yellow-400 transform -rotate-45"></div>
      <div className="absolute top-8 -left-20 w-24 h-1 bg-yellow-400 transform -rotate-30"></div>
      <div className="absolute top-8 -left-20 w-24 h-1 bg-yellow-400 transform -rotate-15"></div>
    </div>
    <div className="absolute bottom-5 left-10">
      <div className="flex flex-col items-center">
        <div className="text-red-500 text-xs">IR Radiation</div>
        <div className="w-20 h-1 bg-red-400 transform rotate-45 my-1"></div>
        <div className="w-20 h-1 bg-red-400 transform rotate-30 my-1"></div>
        <div className="w-20 h-1 bg-red-400 transform rotate-15 my-1"></div>
      </div>
    </div>
    <div className="absolute top-2 left-2 text-sm text-gray-600">
      Solar energy in
    </div>
    <div className="absolute bottom-2 right-2 text-sm text-gray-600">
      Heat energy out
    </div>
  </div>
);

const CarbonCycleVisualization = () => (
  <div className="relative w-full h-full flex items-center justify-center p-4">
    <div className="w-full h-full rounded-lg border border-gray-200 bg-gradient-to-b from-blue-50 to-green-50 overflow-hidden">
      {/* Atmosphere */}
      <div className="absolute top-5 left-0 right-0 h-12 bg-blue-100 bg-opacity-70 flex items-center justify-center">
        <span className="font-medium text-blue-800">Atmosphere (880 Gt)</span>
      </div>

      {/* Ocean surface */}
      <div className="absolute top-24 left-0 right-0 h-16 bg-blue-300 bg-opacity-70 flex items-center justify-center">
        <span className="font-medium text-blue-800">
          Surface Ocean (900 Gt)
        </span>
      </div>

      {/* Deep ocean */}
      <div className="absolute top-44 left-0 right-0 h-24 bg-blue-600 bg-opacity-70 flex items-center justify-center">
        <span className="font-medium text-white">Deep Ocean (37,000 Gt)</span>
      </div>

      {/* Vegetation */}
      <div className="absolute bottom-12 left-5 w-32 h-32 flex flex-col items-center justify-center">
        <div className="w-16 h-24 bg-green-500 rounded-t-full"></div>
        <div className="w-4 h-8 bg-brown-500 bg-gradient-to-b from-yellow-800 to-yellow-900"></div>
        <span className="text-xs font-medium mt-1">
          Vegetation & Soils (2,500 Gt)
        </span>
      </div>

      {/* Fossil fuels */}
      <div className="absolute bottom-5 right-5 w-32 h-28 flex flex-col items-center justify-center">
        <div className="w-20 h-20 bg-gray-800 rounded-sm flex items-center justify-center">
          <span className="text-white text-xs">Fossil Fuels</span>
        </div>
        <span className="text-xs font-medium mt-1">4,000 Gt</span>
      </div>

      {/* Arrows */}
      <svg className="absolute inset-0" width="100%" height="100%">
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="0"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
          </marker>
        </defs>
        <path
          d="M50,20 C50,40 100,40 100,60"
          stroke="#666"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          fill="none"
        />
        <path
          d="M150,60 C150,40 200,40 200,20"
          stroke="#666"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          fill="none"
        />
        <path
          d="M50,60 C50,80 100,80 100,100"
          stroke="#666"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          fill="none"
        />
        <path
          d="M70,100 C70,80 120,80 120,60"
          stroke="#666"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          fill="none"
        />
        <path
          d="M250,20 C270,20 270,100 220,100"
          stroke="#666"
          strokeWidth="2"
          markerEnd="url(#arrowhead)"
          fill="none"
          stroke-dasharray="5,5"
        />
        <text x="280" y="60" fontSize="10" fill="#666">
          Human Activity
        </text>
      </svg>
    </div>
  </div>
);

// Add new visualization components for feedback loops
const IceAlbedoVisualization = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="absolute inset-0 bg-gradient-to-b from-blue-400 to-blue-700 rounded-lg overflow-hidden">
      {/* Sky */}
      <div className="absolute top-0 left-0 right-0 h-1/4 bg-gradient-to-b from-blue-200 to-blue-400"></div>

      {/* Sun */}
      <div className="absolute top-4 right-8">
        <div className="w-12 h-12 rounded-full bg-yellow-300 animate-pulse shadow-lg shadow-yellow-200"></div>
        <div className="absolute top-6 left-0 w-12 h-0.5 bg-yellow-100"></div>
        <div className="absolute top-6 left-1 w-10 h-0.5 bg-yellow-100 transform rotate-12"></div>
        <div className="absolute top-6 left-1 w-10 h-0.5 bg-yellow-100 transform -rotate-12"></div>
      </div>

      {/* Ice layer that can shrink */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-white bg-opacity-90 transition-all duration-1000 ease-in-out">
        <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-blue-100 to-white"></div>

        {/* Arrows showing reflection */}
        <div className="absolute top-12 left-1/4">
          <div className="w-0.5 h-12 bg-yellow-200 transform -rotate-45"></div>
          <div className="w-2 h-2 border-t-2 border-r-2 border-yellow-200 transform rotate-45 -translate-x-1"></div>
        </div>
        <div className="absolute top-12 right-1/4">
          <div className="w-0.5 h-12 bg-yellow-200 transform rotate-45"></div>
          <div className="w-2 h-2 border-t-2 border-l-2 border-yellow-200 transform -rotate-45 translate-x-1"></div>
        </div>
      </div>

      {/* Dark ocean underneath */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-blue-900">
        {/* Arrows showing absorption */}
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
          <div className="w-0.5 h-12 bg-yellow-200"></div>
          <div className="w-2 h-2 border-b-2 border-r-2 border-yellow-200 transform rotate-45 -translate-x-1 translate-y-12"></div>
        </div>
      </div>
    </div>
    <div className="absolute bottom-2 right-2 text-xs text-white font-medium">
      Decreasing ice &rarr; more heat absorbed
    </div>
  </div>
);

const WaterVaporVisualization = () => (
  <div className="relative w-full h-full flex items-center justify-center">
    <div className="absolute inset-0 bg-gradient-to-b from-blue-200 to-blue-500 rounded-lg overflow-hidden">
      {/* Sky */}
      <div className="absolute top-0 left-0 right-0 h-full bg-gradient-to-b from-blue-200 to-blue-400"></div>

      {/* Sun */}
      <div className="absolute top-4 right-8">
        <div className="w-12 h-12 rounded-full bg-yellow-300 animate-pulse shadow-lg shadow-yellow-200"></div>
      </div>

      {/* Ocean */}
      <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-blue-600"></div>

      {/* Water Vapor Clouds */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-48 h-24">
          {/* Multiple cloud shapes */}
          <div className="absolute top-0 left-0 w-16 h-8 bg-white rounded-full opacity-80"></div>
          <div className="absolute top-4 left-8 w-20 h-10 bg-white rounded-full opacity-90"></div>
          <div className="absolute top-0 left-20 w-14 h-7 bg-white rounded-full opacity-80"></div>
          <div className="absolute top-10 left-16 w-16 h-8 bg-white rounded-full opacity-80"></div>
          <div className="absolute top-8 left-4 w-14 h-7 bg-white rounded-full opacity-90"></div>

          {/* Animated vapor arrows */}
          <div className="absolute top-16 left-10 opacity-70">
            <div className="w-0.5 h-8 bg-white transform rotate-6 animate-pulse"></div>
            <div className="absolute top-8 left-0 w-2 h-2 border-b-2 border-r-2 border-white transform rotate-45"></div>
          </div>
          <div className="absolute top-16 left-24 opacity-70">
            <div className="w-0.5 h-8 bg-white transform -rotate-6 animate-pulse"></div>
            <div className="absolute top-8 left-0 w-2 h-2 border-b-2 border-l-2 border-white transform -rotate-45"></div>
          </div>
        </div>
      </div>

      {/* Heat arrows coming back down */}
      <div className="absolute bottom-12 left-1/3">
        <div className="w-0.5 h-10 bg-red-300 transform rotate-12 animate-pulse"></div>
        <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-r-2 border-red-300 transform rotate-45"></div>
      </div>
      <div className="absolute bottom-12 right-1/3">
        <div className="w-0.5 h-10 bg-red-300 transform -rotate-12 animate-pulse"></div>
        <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-red-300 transform -rotate-45"></div>
      </div>
    </div>
    <div className="absolute bottom-2 right-2 text-xs text-white font-medium">
      More water vapor &rarr; more greenhouse effect
    </div>
  </div>
);

// Drag and Drop components
const DragItem = ({ id, content, onDragStart }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, id)}
      className="bg-white p-3 rounded-md shadow mb-2 cursor-move flex items-center"
    >
      <MoveHorizontal className="h-4 w-4 text-gray-400 mr-2" />
      <span>{content}</span>
    </div>
  );
};

const DropTarget = ({
  id,
  expectedValue,
  onDrop,
  isCorrect,
  hasItem,
  itemContent,
}) => {
  const [isOver, setIsOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    const draggedItemId = e.dataTransfer.getData("text/plain");
    onDrop(id, draggedItemId);
  };

  let borderColor = "border-gray-300";
  if (hasItem) {
    borderColor =
      isCorrect === true
        ? "border-green-500"
        : isCorrect === false
        ? "border-red-500"
        : "border-blue-500";
  } else if (isOver) {
    borderColor = "border-blue-400";
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`${borderColor} border-2 rounded-md p-3 min-h-[60px] flex items-center justify-center transition-colors`}
    >
      {hasItem ? (
        <div className="flex items-center justify-between w-full">
          <span>{itemContent}</span>
          {isCorrect === true && <Check className="h-5 w-5 text-green-500" />}
          {isCorrect === false && <X className="h-5 w-5 text-red-500" />}
        </div>
      ) : (
        <span className="text-gray-400">Drop here</span>
      )}
    </div>
  );
};

const ClimateEngine = () => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [activeTab, setActiveTab] = useState("energy-balance");
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [dragItems] = useState([
    { id: "years", content: "years" },
    { id: "decades", content: "decades" },
    { id: "centuries", content: "centuries" },
    { id: "geologic", content: "geologic time" },
  ]);
  const [dropTargets, setDropTargets] = useState([
    {
      id: "atmosphere",
      label: "Atmosphere",
      expectedValue: "years",
      hasItem: false,
      itemContent: "",
      isCorrect: null,
    },
    {
      id: "deep-ocean",
      label: "Deep ocean",
      expectedValue: "centuries",
      hasItem: false,
      itemContent: "",
      isCorrect: null,
    },
    {
      id: "fossil-fuels",
      label: "Fossil fuels",
      expectedValue: "geologic",
      hasItem: false,
      itemContent: "",
      isCorrect: null,
    },
    {
      id: "vegetation",
      label: "Vegetation",
      expectedValue: "decades",
      hasItem: false,
      itemContent: "",
      isCorrect: null,
    },
  ]);
  const [dragComplete, setDragComplete] = useState(false);

  // Add state for sorting game
  const [sortableItems, setSortableItems] = useState([
    { id: "aviation", name: "Aviation & Shipping", percentage: "3%", order: 5 },
    { id: "industry", name: "Industry & F-gases", percentage: "8%", order: 4 },
    { id: "deforestation", name: "Deforestation", percentage: "11%", order: 3 },
    {
      id: "agriculture",
      name: "Agriculture & Food",
      percentage: "16%",
      order: 2,
    },
    { id: "fossil", name: "Fossil Fuels", percentage: "73%", order: 1 },
  ]);
  const [sortingComplete, setSortingComplete] = useState(false);
  const [sortingChecked, setSortingChecked] = useState(false);

  // Add state for feedback quiz
  const [feedbackSelectedOption, setFeedbackSelectedOption] = useState(null);
  const [feedbackShowAnswer, setFeedbackShowAnswer] = useState(false);
  const [feedbackAnsweredCorrectly, setFeedbackAnsweredCorrectly] =
    useState(false);

  // Add state for evidence quiz
  const [evidenceComplete, setEvidenceComplete] = useState(false);

  // Add state for quick-stats completion
  const [statsExplored, setStatsExplored] = useState(false);

  // State to track which sections are completed
  const [completedSections, setCompletedSections] = useState({
    "energy-balance": false,
    "carbon-cycle": false,
    "human-amplifiers": false,
    "feedback-loops": false,
    evidence: false,
    "quick-stats": false,
    "myth-buster": false,
  });

  // State to track if tooltip is visible
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipMessage, setTooltipMessage] = useState("");

  // Handle item drag start
  const handleDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
  };

  // Handle item drop
  const handleDrop = (targetId, itemId) => {
    // Find the item and target
    const item = dragItems.find((item) => item.id === itemId);
    if (!item) return;

    // Update the drop target
    const updatedTargets = dropTargets.map((target) => {
      if (target.id === targetId) {
        return {
          ...target,
          hasItem: true,
          itemContent: item.content,
          isCorrect: target.expectedValue === itemId,
        };
      }
      return target;
    });

    setDropTargets(updatedTargets);

    // Check if all targets have items
    const allTargetsFilled = updatedTargets.every((target) => target.hasItem);
    if (allTargetsFilled) {
      const allCorrect = updatedTargets.every((target) => target.isCorrect);
      if (allCorrect) {
        setDragComplete(true);
      }
    }
  };

  // Reset drag and drop
  const resetDragAndDrop = () => {
    setDropTargets(
      dropTargets.map((target) => ({
        ...target,
        hasItem: false,
        itemContent: "",
        isCorrect: null,
      }))
    );
    setDragComplete(false);
  };

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const checkAnswer = () => {
    setShowAnswer(true);
    if (selectedOption === "c") {
      setUnlocked(true);
    }
  };

  const resetQuiz = () => {
    setSelectedOption(null);
    setShowAnswer(false);
  };

  // Function to handle clicking on a locked tab
  const handleLockedTabClick = (e, tabName) => {
    e.preventDefault();

    // Calculate position for the tooltip
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.bottom + window.scrollY;

    // Set tooltip message based on which tab
    let message = "Complete the current section to unlock this tab";
    if (tabName === "carbon-cycle") {
      message = "Complete the Energy Balance quiz to unlock this section";
    } else {
      const prevSection = getPreviousSection(tabName);
      message = `Complete the ${formatSectionName(
        prevSection
      )} section to unlock this tab`;
    }

    setTooltipPosition({ x, y });
    setTooltipMessage(message);
    setShowTooltip(true);

    // Hide tooltip after 3 seconds
    setTimeout(() => {
      setShowTooltip(false);
    }, 3000);
  };

  // Helper to get the previous section
  const getPreviousSection = (currentTab) => {
    const sections = [
      "energy-balance",
      "carbon-cycle",
      "human-amplifiers",
      "feedback-loops",
      "evidence",
      "quick-stats",
      "myth-buster",
    ];
    const currentIndex = sections.indexOf(currentTab);
    return sections[currentIndex - 1];
  };

  // Helper to format section name for display
  const formatSectionName = (sectionId) => {
    switch (sectionId) {
      case "energy-balance":
        return "Earth's Energy Balance";
      case "carbon-cycle":
        return "Carbon Cycle";
      case "human-amplifiers":
        return "Human Amplifiers";
      case "feedback-loops":
        return "Feedback Loops";
      case "evidence":
        return "Evidence & Attribution";
      case "quick-stats":
        return "Climate Quick-Stats";
      case "myth-buster":
        return "Myth-Buster";
      default:
        return sectionId;
    }
  };

  // Add function to move item up in the sorting list
  const moveItemUp = (index) => {
    if (index === 0) return;
    const newItems = [...sortableItems];
    const temp = newItems[index];
    newItems[index] = newItems[index - 1];
    newItems[index - 1] = temp;
    setSortableItems(newItems);
    setSortingChecked(false);
  };

  // Add function to move item down in the sorting list
  const moveItemDown = (index) => {
    if (index === sortableItems.length - 1) return;
    const newItems = [...sortableItems];
    const temp = newItems[index];
    newItems[index] = newItems[index + 1];
    newItems[index + 1] = temp;
    setSortableItems(newItems);
    setSortingChecked(false);
  };

  // Add function to check if sorting is correct
  const checkSorting = () => {
    const isCorrect = sortableItems.every(
      (item, index) => item.order === index + 1
    );
    setSortingComplete(isCorrect);
    setSortingChecked(true);
  };

  // Handle feedback option selection
  const handleFeedbackOptionSelect = (option) => {
    setFeedbackSelectedOption(option);
  };

  // Check feedback quiz answer
  const checkFeedbackAnswer = () => {
    setFeedbackShowAnswer(true);
    if (feedbackSelectedOption === "b") {
      setFeedbackAnsweredCorrectly(true);
    }
  };

  // Reset feedback quiz
  const resetFeedbackQuiz = () => {
    setFeedbackSelectedOption(null);
    setFeedbackShowAnswer(false);
  };

  // Complete evidence section
  const completeEvidenceSection = () => {
    setEvidenceComplete(true);
    setCompletedSections({
      ...completedSections,
      evidence: true,
    });
  };

  // Complete quick-stats section
  const completeStatsSection = () => {
    setStatsExplored(true);
    setCompletedSections({
      ...completedSections,
      "quick-stats": true,
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          How Earth's Climate Engine Works
          <span className="text-edu-orange">.</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      {/* Module Banner - Fixed the visibility issue */}
      <div className="bg-primary-600 p-8 rounded-lg mb-12">
        <h2 className="text-2xl font-bold mb-4">
          Climate science, demystified.
        </h2>
        <p className="text-lg mb-6">
          By the end of this lesson you'll be able to:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 text-primary-600">
              <span className="text-sm">1</span>
            </div>
            <p>Describe Earth's energy balance in one sentence.</p>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 text-primary-600">
              <span className="text-sm">2</span>
            </div>
            <p>Name the three biggest human sources of greenhouse gases.</p>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 text-primary-600">
              <span className="text-sm">3</span>
            </div>
            <p>Explain two climate feedback loops and why they matter.</p>
          </li>
        </ul>
        <p className="text-sm font-medium border-t border-white pt-4">
          Average seat-time: ≈ 30 minutes.
        </p>
      </div>

      {/* Tab Navigation with tooltip for locked tabs */}
      <div className="flex flex-wrap border-b border-gray-200 mb-8 overflow-x-auto relative">
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeTab === "energy-balance"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveTab("energy-balance")}
        >
          1. Earth's Energy Balance
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeTab === "carbon-cycle"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : unlocked
              ? "text-gray-600 hover:text-primary-500"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={(e) =>
            unlocked
              ? setActiveTab("carbon-cycle")
              : handleLockedTabClick(e, "carbon-cycle")
          }
        >
          {!unlocked && <Lock className="inline h-3 w-3 mr-1" />}
          2. The Carbon Cycle
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeTab === "human-amplifiers"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : unlocked && completedSections["carbon-cycle"]
              ? "text-gray-600 hover:text-primary-500"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={(e) =>
            unlocked && completedSections["carbon-cycle"]
              ? setActiveTab("human-amplifiers")
              : handleLockedTabClick(e, "human-amplifiers")
          }
        >
          {!(unlocked && completedSections["carbon-cycle"]) && (
            <Lock className="inline h-3 w-3 mr-1" />
          )}
          3. Human Amplifiers
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeTab === "feedback-loops"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : unlocked && completedSections["human-amplifiers"]
              ? "text-gray-600 hover:text-primary-500"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={(e) =>
            unlocked && completedSections["human-amplifiers"]
              ? setActiveTab("feedback-loops")
              : handleLockedTabClick(e, "feedback-loops")
          }
        >
          {!(unlocked && completedSections["human-amplifiers"]) && (
            <Lock className="inline h-3 w-3 mr-1" />
          )}
          4. Feedback Loops
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeTab === "evidence"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : unlocked && completedSections["feedback-loops"]
              ? "text-gray-600 hover:text-primary-500"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={(e) =>
            unlocked && completedSections["feedback-loops"]
              ? setActiveTab("evidence")
              : handleLockedTabClick(e, "evidence")
          }
        >
          {!(unlocked && completedSections["feedback-loops"]) && (
            <Lock className="inline h-3 w-3 mr-1" />
          )}
          5. Evidence & Attribution
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeTab === "quick-stats"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : unlocked && completedSections["evidence"]
              ? "text-gray-600 hover:text-primary-500"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={(e) =>
            unlocked && completedSections["evidence"]
              ? setActiveTab("quick-stats")
              : handleLockedTabClick(e, "evidence")
          }
        >
          {!(unlocked && completedSections["evidence"]) && (
            <Lock className="inline h-3 w-3 mr-1" />
          )}
          6. Climate Quick-Stats
        </button>
        <button
          className={`px-4 py-2 whitespace-nowrap ${
            activeTab === "myth-buster"
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : unlocked && completedSections["quick-stats"]
              ? "text-gray-600 hover:text-primary-500"
              : "text-gray-400 cursor-not-allowed"
          }`}
          onClick={(e) =>
            unlocked && completedSections["quick-stats"]
              ? setActiveTab("myth-buster")
              : handleLockedTabClick(e, "myth-buster")
          }
        >
          {!(unlocked && completedSections["quick-stats"]) && (
            <Lock className="inline h-3 w-3 mr-1" />
          )}
          7. Myth-Buster
        </button>

        {/* Tooltip for locked tabs */}
        {showTooltip && (
          <div
            className="absolute z-10 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg text-sm"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y + 10}px`,
              transform: "translateX(-50%)",
            }}
          >
            <div className="relative">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-800"></div>
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 mr-2 text-yellow-400" />
                {tooltipMessage}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="mb-16">
        {activeTab === "energy-balance" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Solar in, heat out — the planetary thermostat
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-2">
                <p className="text-gray-700 mb-4">
                  Earth is constantly hit by short-wave sunlight. About 30%
                  bounces straight back into space, but the rest is absorbed,
                  warming land, ocean and atmosphere. These warm surfaces then
                  emit invisible long-wave (infra-red) radiation upward.
                </p>
                <p className="text-gray-700 mb-4">
                  Greenhouse gases (GHGs) such as CO₂, CH₄ and N₂O absorb part
                  of that IR and re-emit it in all directions, trapping extra
                  heat and raising the planet's surface temperature from a
                  chilly -0.4°C to a life-friendly +15°C.
                </p>

                <div className="bg-gray-50 p-6 rounded-lg mt-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    Key numbers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Variable
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Pre-industrial
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Today
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Δ Radiative forcing*
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            CO₂ (ppm)
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            280
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                            420
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            +2.1 W m⁻²
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            CH₄ (ppb)
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            700
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-semibold">
                            1930
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            +0.6 W m⁻²
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    *Energy added to Earth per square metre of surface.
                  </p>
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg p-4 h-80 flex items-center justify-center lg:col-span-1">
                <div className="text-center h-full w-full">
                  {/* Added Earth's Energy Balance Visualization */}
                  <EnergyBalanceVisualization />
                </div>
              </div>
            </div>

            {/* Quick-check quiz - Improved the selection highlighting */}
            <div className="bg-primary-50 rounded-lg p-6 mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Quick-check (unlock next tab)
              </h3>
              <p className="mb-4">
                Q1. Which type of radiation is trapped by greenhouse gases?
              </p>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handleOptionSelect("a")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    selectedOption === "a"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  a) UV
                </button>
                <button
                  onClick={() => handleOptionSelect("b")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    selectedOption === "b"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  b) Visible
                </button>
                <button
                  onClick={() => handleOptionSelect("c")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    selectedOption === "c"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  c) Infra-red
                </button>
                <button
                  onClick={() => handleOptionSelect("d")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    selectedOption === "d"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  d) Microwaves
                </button>
              </div>

              {!showAnswer ? (
                <button
                  onClick={checkAnswer}
                  disabled={!selectedOption}
                  className={`px-4 py-2 rounded-md text-white ${
                    selectedOption
                      ? "bg-[#349383] hover:bg-primary-700"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Check Answer
                </button>
              ) : (
                <div>
                  {selectedOption === "c" ? (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                      <div className="flex">
                        <Check className="h-5 w-5 text-green-500 mr-2" />
                        <p className="font-medium text-green-800">
                          Correct: c) Infra-red.
                        </p>
                      </div>
                      <p className="mt-2 text-green-700">
                        Why: GHG molecules vibrate at IR wavelengths, absorbing
                        and re-emitting that energy and slowing its escape to
                        space.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                      <div className="flex">
                        <X className="h-5 w-5 text-red-500 mr-2" />
                        <p className="font-medium text-red-800">
                          Incorrect. The correct answer is c) Infra-red.
                        </p>
                      </div>
                      <p className="mt-2 text-red-700">
                        GHG molecules vibrate at IR wavelengths, absorbing and
                        re-emitting that energy and slowing its escape to space.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={resetQuiz}
                    className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 mr-3"
                  >
                    Try Again
                  </button>
                  {unlocked && (
                    <button
                      onClick={() => setActiveTab("carbon-cycle")}
                      className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                    >
                      Continue to The Carbon Cycle
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "carbon-cycle" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              The Carbon Cycle — Moving tonnes around
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <p className="text-gray-700 mb-4">
                  Carbon is always on the move: plants pull it from the air,
                  oceans absorb it, volcanoes & respiration release it. In a
                  pre-industrial world these flows roughly balanced. Human
                  activity now dumps ~11 Gt of extra carbon (40 Gt CO₂) into the
                  air every single year - overwhelming the natural "in-out"
                  equilibrium.
                </p>

                <div className="bg-gray-50 p-6 rounded-lg mt-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">
                    Cheat-sheet reservoirs
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Reservoir
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Mass (Gt C)
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Average residence time
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Atmosphere
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            880
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ~5 years
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Surface ocean
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            900
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            ~6 years
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Deep ocean
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            37 000
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            centuries
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Vegetation & soils
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            2 500
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            decades
                          </td>
                        </tr>
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            Fossil fuels
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            4 000
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                            should stay there
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg p-4 h-80 flex items-center justify-center">
                <div className="text-center h-full w-full">
                  {/* Added Carbon Cycle Visualization */}
                  <CarbonCycleVisualization />
                </div>
              </div>
            </div>

            {/* Implemented drag-and-drop practice */}
            <div className="bg-primary-50 p-6 rounded-lg mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Drag-and-drop practice
              </h3>
              <p className="text-gray-700 mb-4">
                Pair each reservoir with its lifetime: (a) Atmosphere – years;
                (b) Deep ocean – centuries; (c) Fossil fuels – geologic; (d)
                Vegetation – decades.
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">
                    Drag from here:
                  </h4>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    {dragItems.map((item) => (
                      <DragItem
                        key={item.id}
                        id={item.id}
                        content={item.content}
                        onDragStart={handleDragStart}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-2">
                    Drop targets:
                  </h4>
                  <div className="space-y-3">
                    {dropTargets.map((target) => (
                      <div key={target.id} className="flex items-center gap-3">
                        <span className="w-24 text-sm font-medium text-gray-700">
                          {target.label}:
                        </span>
                        <DropTarget
                          id={target.id}
                          expectedValue={target.expectedValue}
                          onDrop={handleDrop}
                          isCorrect={target.isCorrect}
                          hasItem={target.hasItem}
                          itemContent={target.itemContent}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-between">
                <button
                  onClick={resetDragAndDrop}
                  className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                >
                  Reset
                </button>

                {dragComplete && (
                  <div className="flex items-center">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center mr-4">
                      <Check className="h-5 w-5 text-green-500 mr-2" />
                      <p className="text-green-800">
                        Great job! You've correctly matched all items.
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        setCompletedSections({
                          ...completedSections,
                          "carbon-cycle": true,
                        });
                        setActiveTab("human-amplifiers");
                      }}
                      className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                    >
                      Continue to Human Amplifiers
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "human-amplifiers" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Human Amplifiers — Where extra GHGs come from
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Fossil Fuels */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-primary-100 p-4">
                  <h3 className="text-lg font-semibold">Fossil Fuels</h3>
                  <p className="text-sm text-gray-600">
                    Electricity, transport, heat
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 mb-2">
                    Burnt coal, oil and gas add ~73% of all global GHGs (≈ 29 Gt
                    CO₂e yr⁻¹).
                  </p>
                  <p className="text-sm text-primary-600 font-medium">
                    Leverage: renewables, EVs, heat-pumps.
                  </p>
                </div>
              </div>

              {/* Deforestation */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-green-100 p-4">
                  <h3 className="text-lg font-semibold">Deforestation</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 mb-2">
                    Cutting & burning forests releases stored carbon: ~11% of
                    emissions.
                  </p>
                  <p className="text-sm text-primary-600 font-medium">
                    Leverage: zero-deforestation supply chains, agro-forestry.
                  </p>
                </div>
              </div>

              {/* Agriculture & Food */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-yellow-100 p-4">
                  <h3 className="text-lg font-semibold">Agriculture & Food</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 mb-2">
                    Methane from cattle, rice; nitrous oxide from fertiliser =
                    ~16%.
                  </p>
                  <p className="text-sm text-primary-600 font-medium">
                    Leverage: plant-rich diets, precision fertiliser, enteric
                    inhibitors.
                  </p>
                </div>
              </div>

              {/* Industry & F-gases */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-blue-100 p-4">
                  <h3 className="text-lg font-semibold">Industry & F-gases</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 mb-2">
                    Cement, steel, chemicals + refrigerants (HFCs).
                  </p>
                  <p className="text-sm text-primary-600 font-medium">
                    Leverage: green hydrogen, CCS, HFC phase-down.
                  </p>
                </div>
              </div>

              {/* Aviation & Shipping */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-purple-100 p-4">
                  <h3 className="text-lg font-semibold">Aviation & Shipping</h3>
                  <p className="text-sm text-gray-600">Bonus card</p>
                </div>
                <div className="p-4">
                  <p className="text-gray-700 mb-2">
                    Smaller slice (~3%) but hard to decarbonise.
                  </p>
                  <p className="text-sm text-primary-600 font-medium">
                    Leverage: SAF, wind-assist, demand management.
                  </p>
                </div>
              </div>
            </div>

            {/* Implemented sorting game */}
            <div className="bg-primary-50 p-6 rounded-lg mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Sorting game
              </h3>
              <p className="text-gray-700 mb-4">
                Arrange these five sources from largest to smallest global share
                of emissions:
              </p>

              <div className="bg-white rounded-lg p-4 shadow-sm mb-6">
                <div className="space-y-2">
                  {sortableItems.map((item, index) => (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-md border ${
                        sortingChecked
                          ? item.order === index + 1
                            ? "border-green-500 bg-green-50"
                            : "border-red-300 bg-red-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-800 font-semibold mr-3">
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">
                            {item.percentage} of global emissions
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => moveItemUp(index)}
                          disabled={index === 0 || sortingComplete}
                          className={`p-1.5 rounded-md ${
                            index === 0 || sortingComplete
                              ? "text-gray-300"
                              : "text-primary-600 hover:bg-primary-100"
                          }`}
                        >
                          <ArrowUp size={18} />
                        </button>
                        <button
                          onClick={() => moveItemDown(index)}
                          disabled={
                            index === sortableItems.length - 1 ||
                            sortingComplete
                          }
                          className={`p-1.5 rounded-md ${
                            index === sortableItems.length - 1 ||
                            sortingComplete
                              ? "text-gray-300"
                              : "text-primary-600 hover:bg-primary-100"
                          }`}
                        >
                          <ArrowDown size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                {!sortingComplete ? (
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setSortableItems([
                          {
                            id: "aviation",
                            name: "Aviation & Shipping",
                            percentage: "3%",
                            order: 5,
                          },
                          {
                            id: "industry",
                            name: "Industry & F-gases",
                            percentage: "8%",
                            order: 4,
                          },
                          {
                            id: "deforestation",
                            name: "Deforestation",
                            percentage: "11%",
                            order: 3,
                          },
                          {
                            id: "agriculture",
                            name: "Agriculture & Food",
                            percentage: "16%",
                            order: 2,
                          },
                          {
                            id: "fossil",
                            name: "Fossil Fuels",
                            percentage: "73%",
                            order: 1,
                          },
                        ]);
                        setSortingChecked(false);
                      }}
                      className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                    >
                      Reset
                    </button>
                    <button
                      onClick={checkSorting}
                      className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                    >
                      Check Answer
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center mr-4">
                      <Check className="h-5 w-5 text-green-500 mr-2" />
                      <p className="text-green-800">
                        Great job! The correct order is: Fossil Fuels (73%) &gt;
                        Agriculture (16%) &gt; Deforestation (11%) &gt; Industry
                        (8%) &gt; Aviation (3%)
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setCompletedSections({
                          ...completedSections,
                          "human-amplifiers": true,
                        });
                        setActiveTab("feedback-loops");
                      }}
                      className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700 whitespace-nowrap"
                    >
                      Continue to Feedback Loops
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "feedback-loops" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Feedback Loops & Tipping Elements
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {/* Ice-Albedo Loop */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-primary-600 mb-3">
                  Ice–Albedo Loop
                </h3>
                <div className="bg-blue-50 h-40 rounded-lg mb-4 flex items-center justify-center">
                  <IceAlbedoVisualization />
                </div>
                <p className="text-gray-700">
                  When bright ice melts, darker ocean/land absorbs ~90% of
                  sunlight instead of reflecting it. Result: extra warming -
                  more melt. Arctic sea-ice extent has halved since 1980.
                </p>
              </div>

              {/* Water-Vapor Loop */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-primary-600 mb-3">
                  Water-Vapour Loop
                </h3>
                <div className="bg-blue-50 h-40 rounded-lg mb-4 flex items-center justify-center">
                  <WaterVaporVisualization />
                </div>
                <p className="text-gray-700">
                  Warmer air holds ~7% more water vapour per °C, and water
                  vapour itself is a potent GHG. That multiplies the warming
                  triggered by CO₂.
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mt-10 mb-4">
              Tipping-elements gallery
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Amazon Die-back */}
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-center mb-3">
                  <span className="text-4xl">🌳</span>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  Amazon Die-back
                </h4>
                <p className="text-gray-600 text-sm">
                  Beyond 20-25% deforestation, rainfall recycling collapses,
                  converting rainforest to savanna and releasing 90 Gt CO₂.
                </p>
              </div>

              {/* West Antarctic Ice Sheet */}
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-center mb-3">
                  <span className="text-4xl">🧊</span>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  West Antarctic Ice Sheet
                </h4>
                <p className="text-gray-600 text-sm">
                  Warm ocean fingers melt ice shelves from below; loss of
                  buttresses could raise sea level &gt;3 m centuries faster than
                  historic rates.
                </p>
              </div>

              {/* Atlantic Overturning */}
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-center mb-3">
                  <span className="text-4xl">🌊</span>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  Atlantic Overturning (AMOC)
                </h4>
                <p className="text-gray-600 text-sm">
                  Freshwater from Greenland meltwater slows the conveyor that
                  warms Europe; shutdown would shift tropical rainfall belts and
                  deepen N. Atlantic storms.
                </p>
              </div>

              {/* Permafrost Thaw */}
              <div className="bg-white p-5 rounded-lg shadow-md">
                <div className="text-center mb-3">
                  <span className="text-4xl">🐻</span>
                </div>
                <h4 className="font-semibold text-gray-800 mb-2">
                  Permafrost Thaw
                </h4>
                <p className="text-gray-600 text-sm">
                  Frozen soils store twice today's atmospheric carbon; thawing
                  could leak up to 150 Gt CO₂/CH₄ this century - a feedback we
                  can't "turn off."
                </p>
              </div>
            </div>

            {/* Add interactive quiz for this section */}
            <div className="bg-primary-50 p-6 rounded-lg mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Quick Check
              </h3>
              <p className="mb-4">
                Which of the following statements about climate feedback loops
                is correct?
              </p>

              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handleFeedbackOptionSelect("a")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    feedbackSelectedOption === "a"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  a) Feedback loops always decrease the warming effect
                </button>
                <button
                  onClick={() => handleFeedbackOptionSelect("b")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    feedbackSelectedOption === "b"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  b) Positive feedbacks accelerate warming while negative
                  feedbacks slow it down
                </button>
                <button
                  onClick={() => handleFeedbackOptionSelect("c")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    feedbackSelectedOption === "c"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  c) Tipping elements can only be triggered at 2°C of warming
                </button>
                <button
                  onClick={() => handleFeedbackOptionSelect("d")}
                  className={`w-full text-left px-4 py-2 rounded-md border ${
                    feedbackSelectedOption === "d"
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50"
                      : "border-gray-300 hover:border-primary-300"
                  }`}
                >
                  d) None of the feedback loops in Earth's climate system are
                  irreversible
                </button>
              </div>

              {!feedbackShowAnswer ? (
                <button
                  onClick={checkFeedbackAnswer}
                  disabled={!feedbackSelectedOption}
                  className={`px-4 py-2 rounded-md text-white ${
                    feedbackSelectedOption
                      ? "bg-[#349383] hover:bg-primary-700"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Check Answer
                </button>
              ) : (
                <div>
                  {feedbackSelectedOption === "b" ? (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                      <div className="flex">
                        <Check className="h-5 w-5 text-green-500 mr-2" />
                        <p className="font-medium text-green-800">
                          Correct! Positive feedbacks amplify the initial
                          change, while negative feedbacks slow it down.
                        </p>
                      </div>
                      <p className="mt-2 text-green-700">
                        Ice-albedo and water vapor are both positive feedback
                        loops that amplify warming.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                      <div className="flex">
                        <X className="h-5 w-5 text-red-500 mr-2" />
                        <p className="font-medium text-red-800">
                          Incorrect. The correct answer is b) Positive feedbacks
                          accelerate warming while negative feedbacks slow it
                          down.
                        </p>
                      </div>
                      <p className="mt-2 text-red-700">
                        In climate science, "positive" doesn't mean good - it
                        means the feedback reinforces the initial change.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={resetFeedbackQuiz}
                    className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 mr-3"
                  >
                    Try Again
                  </button>
                  {feedbackAnsweredCorrectly && (
                    <button
                      onClick={() => {
                        setCompletedSections({
                          ...completedSections,
                          "feedback-loops": true,
                        });
                        setActiveTab("evidence");
                      }}
                      className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                    >
                      Continue to Evidence & Attribution
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "evidence" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Evidence & Attribution — How we know it's us
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Ocean Heat Content */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-blue-100 h-40 rounded-lg mb-4 flex items-center justify-center">
                  <CloudRain className="h-12 w-12 text-blue-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Ocean Heat Content
                </h3>
                <p className="text-gray-700">
                  The upper 2 000 m of ocean stores 90% of excess heat; each
                  decade beats the previous. Satellites can't "bias" the deep
                  sea.
                </p>
              </div>

              {/* Outgoing Radiation Fingerprint */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-red-100 h-40 rounded-lg mb-4 flex items-center justify-center">
                  <Thermometer className="h-12 w-12 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Outgoing Radiation Fingerprint
                </h3>
                <p className="text-gray-700">
                  Satellites measure fewer IR photons escaping at GHG absorption
                  bands (CO₂ at 15 µm, CH₄ at 7.6 µm) - direct evidence of
                  enhanced trapping.
                </p>
              </div>

              {/* Cooling Stratosphere */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-purple-100 h-40 rounded-lg mb-4 flex items-center justify-center">
                  <Wind className="h-12 w-12 text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Cooling Stratosphere, Warming Troposphere
                </h3>
                <p className="text-gray-700">
                  If the Sun were driving current warming both layers would
                  heat; only GHG theory predicts this opposite pattern - exactly
                  what weather balloons see since the 1970s.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                "Spot-the-difference" task
              </h3>
              <div className="bg-gray-100 p-4 rounded-lg mb-4 h-60 flex items-center justify-center">
                <p className="text-gray-600">
                  Two world maps comparing natural vs. all forcings would be
                  displayed here
                </p>
              </div>
              <p className="text-gray-700">
                Learners click the largest anomalies (high-latitude land).
                <span className="font-medium ml-2">
                  Pop-up: "Human emissions warm land faster than ocean; Arctic
                  heats ≈ 4× global mean."
                </span>
              </p>

              {/* Add continue button */}
              <div className="flex justify-end mt-6">
                {!evidenceComplete ? (
                  <button
                    onClick={completeEvidenceSection}
                    className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                  >
                    I've Reviewed the Evidence
                  </button>
                ) : (
                  <button
                    onClick={() => setActiveTab("quick-stats")}
                    className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                  >
                    Continue to Climate Quick-Stats
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "quick-stats" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Climate Quick-Stats (live tiles)
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Global Temp Anomaly */}
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Thermometer className="h-10 w-10 mx-auto text-red-500 mb-2" />
                <h3 className="text-lg font-medium text-gray-800 mb-3">
                  Global Temp Anomaly
                </h3>
                <p className="text-3xl font-bold text-primary-600 mb-1">
                  +1.21°C
                </p>
                <p className="text-xs text-gray-500">(vs 1850-1900)</p>
                <p className="text-sm text-gray-600 mt-3">
                  Hottest 10 years all occurred since 2014.
                </p>
              </div>

              {/* CO₂ @ Mauna Loa */}
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <Sun className="h-10 w-10 mx-auto text-yellow-500 mb-2" />
                <h3 className="text-lg font-medium text-gray-800 mb-3">
                  CO₂ @ Mauna Loa
                </h3>
                <p className="text-3xl font-bold text-primary-600 mb-1">
                  426 ppm
                </p>
                <p className="text-sm text-gray-600 mt-3">
                  Highest in at least 2 million years.
                </p>
              </div>

              {/* Sea-level Rise */}
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <CloudRain className="h-10 w-10 mx-auto text-blue-500 mb-2" />
                <h3 className="text-lg font-medium text-gray-800 mb-3">
                  Sea-level Rise
                </h3>
                <p className="text-3xl font-bold text-primary-600 mb-1">
                  3.7 mm/yr
                </p>
                <p className="text-sm text-gray-600 mt-3">
                  Twice 20th-century rate; mainly thermal expansion + ice loss.
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-500 italic mt-4 mb-6">
              * Values updated monthly via NOAA / NASA APIs.
            </p>

            {/* Add continue button */}
            <div className="flex justify-end mt-6">
              {!statsExplored ? (
                <button
                  onClick={completeStatsSection}
                  className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                >
                  I've Reviewed the Statistics
                </button>
              ) : (
                <button
                  onClick={() => setActiveTab("myth-buster")}
                  className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                >
                  Continue to Myth-Buster
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === "myth-buster" && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
              Myth-Buster Checkpoint
            </h2>
            <p className="text-gray-700 mb-4">Learner must score ≥ 80%.</p>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Statement
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      True / False
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Explainer
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                      "Volcanoes emit more CO₂ than humans."
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      False
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      Annual volcanic CO₂ ≈ 0.3 Gt vs human 40 Gt.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                      "The ozone hole causes global warming."
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      False
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      Ozone loss cools stratosphere; warming is driven by GHGs.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                      "Water vapour is the most abundant GHG."
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 font-medium">
                      True – but
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      It responds to temperature; CO₂ controls the thermostat.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                      "97%+ of actively publishing climate scientists agree
                      humans are warming the planet."
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      True
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      Meta-analyses of 11 000 papers show consensus.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-900">
                      "The sun's energy output has declined slightly since
                      1978."
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      True
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-500">
                      Yet Earth keeps warming — further proof it's GHGs, not
                      solar.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 border border-green-200 p-6 rounded-lg mt-8 text-center">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                Badge awarded: Climate-Engine Sleuth
              </h3>
              <p className="text-green-700">
                You've mastered the mechanics — now see them in action.
              </p>
              <p className="text-green-700 mt-2">
                Tap "Next: Carbon Emissions 101" to learn how we track carbon
                and measure your own impact.
              </p>

              {/* Add final completion button */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    setCompletedSections({
                      ...completedSections,
                      "myth-buster": true,
                    });
                    setIsCompleted(true);
                    // In a real app, navigate to the next module
                  }}
                  className="px-6 py-3 rounded-md bg-[#349383] text-white hover:bg-primary-700 font-medium"
                >
                  Complete Module & Continue to Carbon Emissions 101
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/why-it-matters"
        prevText="Previous: Why It Matters"
        nextLink="/carbon-emissions-101"
        nextText="Next: Carbon Emissions 101"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted || completedSections["myth-buster"]}
      />
    </div>
  );
};

export default ClimateEngine;
